import './PrematchDetail.less';

import React, { memo, useCallback, useEffect, useRef } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import i18n from '@i18n';
import Article from '@/article/views/Article';
import BetbookPostsModule from '@/betbook/views/BetbookPostsModule';
import { isProfileDiscussionEnabled } from 'nike-ui/src/profiles';
import { BETBOOK } from '@/nikeone/context/BoxOptionsContext';
import { denormalizeSportEventId } from '@/nikeone/domain/sportevent';
import useBoxStore from '@/nikeone/hook/useBoxStore';
import { MatchNotifyPane } from '@/notifications/components/MatchNotifyDialog';
import useMatchNotification from '@/notifications/components/useMatchNotification';
import TrackRecentVisit from '@/recentlyvisited/views/TrackRecentVisit';
import { MatchBoardPrematch } from './MatchBoard';
import PrematchDetailBets from './PrematchDetailBets';
import { MdForumContextProvider } from '@/markdown/modules/MdForumContext';
import DiscussionComments from '@/markdown/modules/DiscussionComments';
import { clearStatistics, useNikeoneEventLoad } from '@/nikeone/handlers-detail';
import { detailData$ } from '@/nikeone/subs-detail';
import { clearAnalysisCount, getAnalysisCountForEvent, retrieveDiscussionCountForEvent } from '@/nikeone/handlers';
import BetradarStatisticsTabs from 'nike-ui/src/components/match-statistics/utils';
import MarketStatistics from '@/market-statistics';
import SportEventContext from 'nike-ui/src/common/SportEventContext';
import MatchStatisticsContent from 'nike-ui/src/components/match-statistics/views/MatchStatisticsContent';
import MatchStatisticsTabs from 'nike-ui/src/components/match-statistics/views/MatchStatisticsTabs';
import Tabs, { TabButton, useTabs } from 'nike-ui/src/components/turboLive/components/Tabs';
import { clearTicktusActiveMatch } from '@/mytickets/active/handlers';
import BetLocationContext, { betLocation } from 'nike-ui/src/common/BetLocationContext';
import { ErrorBoundary } from 'nike-ui/src/common/ErrorBoundary';
import { getStreamIds, startInfoCentre } from 'nike-ui/src/components/turboLive/push/overview/handlers';
import { useLoadMatchtrackerTemplates } from 'nike-ui/src/components/match-statistics/hooks/useLoadMatchtrackerTemplates';
import { useIsBetradarWidgetAvailable } from 'nike-ui/src/components/match-statistics/hooks/useIsBetradarWidgetAvailable';
import TurboLiveProvider from '@/turbo-live/context/TurboLiveProvider';
import DetailHeaderMedia from '@/matchdetail/views/DetailHeaderMedia';
import useRemoveMarketStats from 'nike-ui/src/components/market-statistics/hooks/useRemoveMarketStats';
import useMatchMarketStats from 'nike-ui/src/components/market-statistics/hooks/useMatchMarketStats';
import { postsCount$ } from '@/nikeone/subs';
import { BetradarMTScoreboardLoadable } from '@/matchtracker/RadarLoadable';
import { getLayout } from 'nike-ui/src/components/turboLive/utils';
import { Footer } from '@/common/components/footer';
import { SharedIntersectionObserverProvider } from 'nike-ui/src/hooks/shared-intersection-observer/provider';
import { useSharedIntersectionObserver } from 'nike-ui/src/hooks/shared-intersection-observer/hook';
import useMarketStatsAvailable from 'nike-ui/src/components/market-statistics/hooks/useMarketStatsAvailable';
import { getSportNameById } from 'nike-ui/src/common/utils/utils';

const PrematchDetail = () => {
    const ref = useRef(null);
    const intersectionObserverOptions = useRef({
        root: ref.current
    });
    return (
        <SharedIntersectionObserverProvider options={intersectionObserverOptions.current}>
            <div ref={ref} className="flex flex-col prematch-detail">
                <PrematchDetailView />
            </div>
        </SharedIntersectionObserverProvider>
    );
};

function PrematchDetailView() {
    const ref = useRef(null);
    const headerRef = useRef(null);
    const { derefSub } = useReframe();
    useLoadMatchtrackerTemplates();
    const detailData = derefSub(detailData$);
    const betbookCount = derefSub(postsCount$);
    const discussionCount = derefSub(['nikeone/detail/discussionPostsCount']);
    const sportEvent = detailData.get('sportEvent');
    const sportEventId = sportEvent.get('sportEventId');
    const marketStatsSize = useMatchMarketStats(sportEventId, false);
    const articleNiceUrl = sportEvent.get('articleNiceUrl');
    const sportId = sportEvent.get('sportId');
    const statisticsId = sportEvent.get('statisticsId');
    const tournamentId = String(sportEvent.get('tournamentId'));
    const title = sportEvent.get('participants');
    const expiration = sportEvent.get('expiration');
    const sportName = getSportNameById(sportId, false);
    const discussionEnabled = isProfileDiscussionEnabled();
    useRemoveMarketStats(sportEventId);
    useBoxStore('betbook', BETBOOK);
    useEffect(() => {
        if (sportEvent) {
            dispatch(startInfoCentre(sportEvent));
        }
    }, [sportEvent]);
    useEffect(() => {
        dispatch(getAnalysisCountForEvent(sportEventId));
        if (discussionEnabled) {
            dispatch(retrieveDiscussionCountForEvent(sportEventId));
        }
        return () => {
            dispatch(clearStatistics);
            dispatch(clearTicktusActiveMatch);
            dispatch(clearAnalysisCount);
        };
    }, [discussionEnabled, sportEventId]);
    const [onTabClick, setArrowVisible, wrapRef, scrollElementRef, active, leftArrow, rightArrow, repaint] = useTabs(
        'detailBets'
    );
    useEffect(() => {
        const scrollNode = ref.current;
        const offsetTop = headerRef.current?.offsetHeight || 0;
        if (scrollNode?.scrollTop > offsetTop) {
            scrollNode.scrollTo(0, offsetTop);
        }
    }, [active]);
    const [obRef, visibleHeader] = useSharedIntersectionObserver();
    const isBRWidgetAvailable = useIsBetradarWidgetAvailable(sportEvent);
    const [betradarStreamId, ufcStreamId] = getStreamIds(sportEvent);
    const parsedUfcStream = ufcStreamId?.split(',');
    const ufcEventId = parsedUfcStream && parsedUfcStream[0];
    const parsedBetradarStreamId = betradarStreamId || (parsedUfcStream && parsedUfcStream[1]);
    const sportHasMarketStats = useMarketStatsAvailable(sportId);

    const showMarketStatsTab = sportHasMarketStats && marketStatsSize > 0;
    // FIXME: isBRWidgetAvailable a infocentrumTabs - volany 2 krat ten isty hook, wut?
    const infocentrumTabs = useIsBetradarWidgetAvailable(sportEvent);

    const handleGameState = () => {
        if (!sportEvent) {
            return null;
        }
        if (sportEvent.get('isLive')) {
            return 'LIVE';
        } else if (detailData.get('box').get('resultBetIds').size >= 1) {
            return 'RESULTS';
        }
        return 'PREMATCH';
    };

    const gameState = handleGameState();

    return (
        <>
            <MatchBoardPrematch sportEvent={sportEvent} sportName={sportName} visibleHeader={visibleHeader} />
            <div
                ref={ref}
                className="flex-1 flex flex-col overflow-auto"
                data-sport={sportName}
                data-tournament={sportEvent.get('tournamentName')}
                data-game-state={gameState}
                data-participants={sportEvent.get('participants').join(' - ')}
            >
                <span className="p-rel prematch-detail-ob" ref={obRef} />
                <TrackRecentVisit
                    sportEventId={denormalizeSportEventId(sportEventId).toString()}
                    sportId={sportId}
                    expiration={expiration}
                    participants={title}
                />
                <div ref={headerRef} className={`bg-${sportId}`}>
                    <TurboLiveProvider>
                        <DetailHeaderMedia
                            tournamentId={tournamentId}
                            sportId={parseInt(sportId)}
                            eventId={sportEventId}
                            sportCode={sportName}
                            statisticsId={statisticsId}
                            sportEvent={sportEvent}
                        />
                    </TurboLiveProvider>
                </div>
                <SportEventContext.Provider value={sportEvent}>
                    <Tabs
                        leftArrowVisible={leftArrow}
                        rightArrowVisible={rightArrow}
                        setArrowVisible={setArrowVisible}
                        wrapRef={wrapRef}
                        otherClass="tabs-shadow prematch-detail-tabs"
                        repaint={repaint}
                    >
                        <TabButton
                            name="detailBets"
                            label={i18n('react.page.title.nikeone')}
                            active={active}
                            trackData={{ category: 'Ponuka', action: 'Kurzy', label: 'Detail zapasu' }}
                            onTabChange={onTabClick}
                            scrollRef={scrollElementRef}
                        />
                        {showMarketStatsTab && (
                            <TabButton
                                name="marketStatistics"
                                data-atid="n1-detail-tabs-marketing-statistics"
                                label={i18n('marketStatistics.TabText')}
                                active={active}
                                trackData={{
                                    category: 'Ponuka',
                                    action: 'Marketingove Statistiky',
                                    label: 'Detail zapasu'
                                }}
                                onTabChange={onTabClick}
                                scrollRef={scrollElementRef}
                                badgeCount={marketStatsSize}
                            />
                        )}
                        {isBRWidgetAvailable && parsedBetradarStreamId && (
                            <MatchStatisticsTabs
                                eventId={sportEventId}
                                scrollElementRef={scrollElementRef}
                                wrapRef={wrapRef}
                                setArrowVisible={setArrowVisible}
                                active={active}
                                leftArrow={leftArrow}
                                rightArrow={rightArrow}
                                onTabClick={onTabClick}
                                sportId={sportId}
                                infocentrumTabs={infocentrumTabs}
                            />
                        )}
                        <TabButton
                            name="betbookAnalyses"
                            label={i18n('betradarMT.tabs.betbookAnalyses')}
                            active={active}
                            trackData={{ category: 'Ponuka', action: 'Analýzy', label: 'Detail zapasu' }}
                            onTabChange={onTabClick}
                            badgeCount={betbookCount}
                            scrollRef={scrollElementRef}
                        />
                        {discussionEnabled && (
                            <TabButton
                                name="Discussion"
                                label={i18n('prematch.tabs.discussion')}
                                active={active}
                                trackData={{ category: 'Ponuka', action: 'Diskusia', label: 'Detail zapasu' }}
                                onTabChange={onTabClick}
                                badgeCount={discussionCount}
                                scrollRef={scrollElementRef}
                            />
                        )}
                        <NotificationTab
                            activeTab={active}
                            setTab={onTabClick}
                            sportEventId={sportEventId}
                            scrollElementRef={scrollElementRef}
                        />
                    </Tabs>
                </SportEventContext.Provider>
                {active === 'detailAnalyses' && articleNiceUrl ? (
                    <Article articleNiceUrl={articleNiceUrl} type="analyse" />
                ) : active === 'notifications' ? (
                    <MatchNotifyPane eventId={sportEventId} />
                ) : active === 'betbookAnalyses' ? (
                    <BetLocationContext.Provider value={{ betLocationSection: betLocation.KURZY_DETAIL_ANALYSIS }}>
                        <BetbookPostsModule matchId={sportEventId} isResult={gameState === 'RESULTS'} />
                    </BetLocationContext.Provider>
                ) : active === 'Discussion' ? (
                    <Discussion subjectType="MATCH" subjectId={sportEventId} />
                ) : active === 'marketStatistics' ? (
                    <BetLocationContext.Provider
                        value={{ betLocationSection: betLocation.KURZY_DETAIL_OSTATNI_TIPUJU_PREMATCH }}
                    >
                        <MarketStatistics matchId={sportEventId} sportId={sportId} sportEvent={sportEvent} />
                    </BetLocationContext.Provider>
                ) : Object.values(BetradarStatisticsTabs).includes(active) ? (
                    <MatchStatisticsContent
                        active={active}
                        sportId={sportId}
                        matchId={parsedBetradarStreamId}
                        eventId={sportEventId}
                        tournamentId={tournamentId}
                        ufcEventId={ufcEventId}
                    />
                ) : (
                    <ErrorBoundary description={'prematch-detail'}>
                        <BetLocationContext.Provider value={{ betLocationSection: betLocation.KURZY_DETAIL_PREMATCH }}>
                            <PrematchDetailBets detailData={detailData} />
                        </BetLocationContext.Provider>
                    </ErrorBoundary>
                )}
                <Footer />
            </div>
        </>
    );
}

export default memo(PrematchDetail);

export function PromoDetail({ matchId, isPromoPage = false }) {
    const { derefSub } = useReframe();
    const detailData = derefSub(detailData$);
    const sportEvent = detailData?.get('sportEvent');
    useNikeoneEventLoad(matchId, isPromoPage);

    if (!detailData || !sportEvent) {
        return null;
    }

    const sportName = sportEvent.get('sportName');
    const layout = getLayout(sportName)?.toLowerCase();

    return (
        <>
            <div className={`bg-${sportEvent.get('sportId')}`}>
                <BetradarMTScoreboardLoadable
                    sportId={sportEvent.get('sportId')}
                    event={sportEvent}
                    sportCode={layout}
                />
            </div>
            <PrematchDetailBets detailData={detailData} betbookActive={false} promoActive={true} />
        </>
    );
}

export function NotificationTab({ activeTab, setTab, sportEventId, scrollElementRef }) {
    const [supported, subscribed, notificationTypes, setTabNotifications] = useMatchNotification(
        sportEventId,
        useCallback(() => {
            setTab(null, 'notifications');
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [setTab])
    );

    if (!supported || !notificationTypes || !notificationTypes.length) {
        return null;
    }
    return (
        <TabButton
            name="notifications"
            label={i18n('prematch.tabs.notifications')}
            active={activeTab}
            trackData={{ category: 'Ponuka', action: 'Notifikácie', label: 'Detail zapasu' }}
            onTabChange={setTabNotifications}
            badgeCount={subscribed}
            className="p-rel"
            scrollRef={scrollElementRef}
        />
    );
}

export function Discussion({ subjectId, subjectType }) {
    return (
        <MdForumContextProvider subjectType={subjectType} subjectId={subjectId}>
            <DiscussionComments />
        </MdForumContextProvider>
    );
}
